import type {
    IDeliveryAttributes,
    IOffice,
    TOfficeDeliveryType,
    ICourier,
    TDeliveryType,
    TOfficeOption,
    TDeliveryFinishArgs,
    ISelectedTimeSlot,
    IDeliveriesFinishCourierArgs,
    IDeliveriesFinishAnyOfficeArgs,
    IDeliveriesFinishSpecificOfficeArgs,
    IFinishResponse,
} from '../models';

export const isReferralProgram = (obj: IFinishResponse): obj is IFinishResponse => {
    return obj && (obj as IFinishResponse).referralProgram !== undefined;
};

export const checkIsCardUnnamed = ({
    namedCardEnabled,
    unnamedCardEnabled,
}: IDeliveryAttributes['attributes']) => unnamedCardEnabled || !namedCardEnabled;

interface IGetFinishCourierRequestDataArgs {
    additionalProducts?: TDeliveryFinishArgs['additionalProducts'];
    additionalServices?: TDeliveryFinishArgs['additionalServices'];
    appId: string;
    brokerageAccountRequired?: boolean;
    businessUid: string;
    codeWord?: string;
    comment?: string;
    courier?: ICourier;
    deliveryAddress?: string;
    selectedTimeSlot?: ISelectedTimeSlot;
}

export type TGetFinishCourierRequestData = (
    args: IGetFinishCourierRequestDataArgs
) => IDeliveriesFinishCourierArgs | null;

export const getFinishCourierRequestData: TGetFinishCourierRequestData = ({
    additionalProducts,
    additionalServices,
    appId,
    brokerageAccountRequired,
    businessUid,
    codeWord,
    comment,
    courier,
    deliveryAddress,
    selectedTimeSlot,
}) => {
    if (!selectedTimeSlot || !courier || !deliveryAddress) {
        return null;
    }

    const { deliveryDate, express, slotFrom, slotTo, uuid } = selectedTimeSlot;

    return {
        appId,
        codeWord,
        businessUid,
        isUnnamedCard: checkIsCardUnnamed(courier.attributes),
        deliveryUuid: uuid,
        courierDelivery: {
            brokerageAccountRequired: !!brokerageAccountRequired,
            address: deliveryAddress,
            additionalProducts,
            additionalServices,
            date: deliveryDate,
            comment,
            slotFrom,
            slotTo,
            express,
        },
    };
};

interface IGetFinishRequestDataArgs {
    additionalProducts?: TDeliveryFinishArgs['additionalProducts'];
    additionalServices?: TDeliveryFinishArgs['additionalServices'];
    appId: string;
    brokerageAccountRequired?: boolean;
    businessUid: string;
    codeWord?: string;
    comment?: string;
    courier?: ICourier;
    deliveryAddress?: string;
    deliveryType: TDeliveryType;
    officeDeliveryType: TOfficeDeliveryType;
    offices?: IOffice[];
    selectedOffice?: TOfficeOption;
    selectedTimeSlot?: ISelectedTimeSlot;
}

export type TGetFinishRequestData = (args: IGetFinishRequestDataArgs) => TDeliveryFinishArgs | null;

interface IGetFinishAnyOfficeRequestDataArgs {
    appId: string;
    businessUid: string;
    codeWord?: string;
    offices?: IOffice[];
}

export type TGetFinishAnyOfficeRequestData = (
    args: IGetFinishAnyOfficeRequestDataArgs
) => IDeliveriesFinishAnyOfficeArgs | null;

export const getFinishAnyOfficeRequestData: TGetFinishAnyOfficeRequestData = ({
    appId,
    businessUid,
    codeWord,
    offices,
}) => {
    if (!offices) {
        return null;
    }

    return {
        appId,
        codeWord,
        businessUid,
        isUnnamedCard: checkIsCardUnnamed(offices[0]?.attributes),
        officeDelivery: {
            obtainingOffice: null,
        },
    };
};

interface IGetFinishSpecificOfficeRequestDataArgs {
    appId: string;
    businessUid: string;
    codeWord?: string;
    offices?: IOffice[];
    selectedOffice?: TOfficeOption;
}

export type TGetFinishSpecificOfficeRequestData = (
    args: IGetFinishSpecificOfficeRequestDataArgs
) => IDeliveriesFinishSpecificOfficeArgs | null;

export const getFinishSpecificOfficeRequestData: TGetFinishSpecificOfficeRequestData = ({
    appId,
    businessUid,
    codeWord,
    offices,
    selectedOffice,
}) => {
    const office = offices?.find(officeItem => officeItem.pid === selectedOffice?.key);

    if (!office) {
        return null;
    }

    return {
        appId,
        codeWord,
        businessUid,
        deliveryUuid: office.uuid,
        isUnnamedCard: checkIsCardUnnamed(office.attributes),
        officeDelivery: {
            obtainingOffice: office.pid,
        },
    };
};

export const getFinishRequestData: TGetFinishRequestData = ({
    additionalProducts,
    additionalServices,
    appId,
    brokerageAccountRequired,
    businessUid,
    codeWord,
    comment,
    courier,
    deliveryAddress,
    deliveryType,
    officeDeliveryType,
    offices,
    selectedOffice,
    selectedTimeSlot,
}) => {
    const commonData = {
        appId,
        businessUid,
        codeWord,
    };

    if (deliveryType === 'courier') {
        return getFinishCourierRequestData({
            ...commonData,
            selectedTimeSlot,
            courier,
            brokerageAccountRequired,
            comment,
            deliveryAddress,
            additionalProducts,
            additionalServices,
        });
    }

    if (deliveryType === 'offices' && officeDeliveryType === 'any') {
        return getFinishAnyOfficeRequestData({ ...commonData, offices });
    }

    if (deliveryType === 'offices' && officeDeliveryType === 'specific') {
        return getFinishSpecificOfficeRequestData({
            ...commonData,
            offices,
            selectedOffice,
        });
    }

    return null;
};
